import { useCallback, useState } from 'react';
import { mensagemDoErro } from '../api/erros.js';
import {
  definirSituacaoDaIntegracao,
  obterIntegracao,
  removerCredencialDoErp,
  salvarIntegracao,
  SISTEMAS,
  testarConexaoComErp,
} from '../api/integracao-erp.js';
import type { ConfiguracaoComCredencial, DadosDaConfiguracao } from '../api/integracao-erp.js';
import { codigoDoErro } from '../api/erros.js';
import { useConsulta } from '../api/useConsulta.js';
import { usePode } from '../autorizacao/usePode.js';
import { AvisoDaTela } from '../componentes/AvisoDaTela.js';
import type { TomDoAviso } from '../componentes/AvisoDaTela.js';
import { Botao } from '../componentes/Botao.js';
import { CampoSelecao, CampoTexto } from '../componentes/Campo.js';
import { Cartao } from '../componentes/Cartao.js';
import { ChipStatus } from '../componentes/ChipStatus.js';
import { DialogoConfirmacao } from '../componentes/DialogoConfirmacao.js';
import { formatarDataHora } from '../componentes/datas.js';
import { EstadoVazio } from '../componentes/EstadoVazio.js';
import { IconeConfiguracoes } from '../componentes/Icones.js';
import { Pagina } from '../componentes/Pagina.js';
import { Resultado } from '../componentes/Resultado.js';
import { seHouver } from '../componentes/props.js';
import { FormularioDaCredencial } from './configuracoes/FormularioDaCredencial.js';
import { IdentificacaoNoArmazem } from './configuracoes/IdentificacaoNoArmazem.js';
import { LeituraPorImagem } from './configuracoes/LeituraPorImagem.js';
import './Configuracoes.css';

/**
 * Enquanto ninguém configurou, a API responde 404 com este código. Não é falha:
 * é o estado inicial de todo cliente novo.
 */
const NAO_CONFIGURADA = 'INTEGRACAO_NAO_CONFIGURADA';

type EstadoDaTela =
  | { readonly configurada: false }
  | { readonly configurada: true; readonly configuracao: ConfiguracaoComCredencial };

type Aviso = { readonly tom: TomDoAviso; readonly texto: string };

const MODOS = [
  { valor: '2', rotulo: '2 — credencial criptografada' },
  { valor: '0', rotulo: '0 — sem criptografia (exige https)' },
];

const ORIGENS = [
  { valor: 'mock', rotulo: 'Fixtures (mock) — nada sai para o ERP' },
  { valor: 'erp', rotulo: 'ERP do cliente' },
];

/** O que o formulário guarda enquanto está sendo digitado: tudo texto. */
type Rascunho = {
  readonly sistema: string;
  readonly endpointBase: string;
  readonly modoAutenticacao: string;
  readonly codEmp: string;
  readonly codFiliais: string;
  readonly identificadorSistema: string;
  readonly timeoutMs: string;
  readonly tamanhoLote: string;
  readonly origem: string;
};

const RASCUNHO_INICIAL: Rascunho = {
  sistema: 'SeniorErp5',
  endpointBase: '',
  modoAutenticacao: '2',
  codEmp: '',
  codFiliais: '',
  identificadorSistema: '',
  timeoutMs: '30000',
  tamanhoLote: '50',
  origem: 'mock',
};

function rascunhoDe(configuracao: ConfiguracaoComCredencial): Rascunho {
  return {
    sistema: configuracao.sistema,
    endpointBase: configuracao.endpointBase,
    modoAutenticacao: String(configuracao.modoAutenticacao),
    codEmp: String(configuracao.codEmp),
    codFiliais: configuracao.codFiliais.join(', '),
    identificadorSistema: configuracao.identificadorSistema,
    timeoutMs: String(configuracao.timeoutMs),
    tamanhoLote: String(configuracao.tamanhoLote),
    origem: configuracao.usarMock ? 'mock' : 'erp',
  };
}

/**
 * Lista de filiais digitada à mão: `1, 2 3;4` vira `[1, 2, 3, 4]`.
 *
 * Separador solto de propósito — vírgula, espaço e ponto e vírgula. Quem cola a
 * lista de um e-mail do consultor não deveria descobrir o separador certo por
 * tentativa. Item que não é número inteiro vira `NaN` e é recusado pela
 * validação de quem chama, em vez de sumir em silêncio.
 */
export function filiaisDe(texto: string): readonly number[] {
  return texto
    .split(/[\s,;]+/u)
    .filter((parte) => parte !== '')
    .map((parte) => (/^\d+$/u.test(parte) ? Number(parte) : Number.NaN));
}

/**
 * Integração com o ERP (RF-089, Anexo A.8).
 *
 * A tela existe para tirar do código o que é de cada cliente: qual ERP, em que
 * endereço, com qual empresa e quais filiais. Três decisões dela não são de
 * estilo:
 *
 * - **a credencial mora em outro cartão, e a senha nunca é exibida.** Ela vai
 *   para um cofre gerenciado e não volta em resposta nenhuma (RNF-073). O que
 *   a tela mostra é o ESTADO — de quem é e desde quando;
 * - **ligar a integração é um passo à parte de salvar.** Salvar parâmetros não
 *   pode, sozinho, abrir tráfego para o sistema do cliente; e ligar sem
 *   credencial é recusado pela API com `CREDENCIAL_ERP_AUSENTE`;
 * - **o teste de conexão não prova a credencial.** Ele pergunta se o middleware
 *   Senior responde, sem mandar usuário nem senha. Dizer "conexão OK" com a
 *   senha errada mandaria quem opera procurar defeito em qualquer outro lugar.
 */
export function Configuracoes() {
  const pode = usePode();
  const podeGerenciar = pode('integracao.gerenciar');

  const [rascunho, definirRascunho] = useState<Rascunho | null>(null);
  const [erros, definirErros] = useState<Readonly<Record<string, string>>>({});
  const [aviso, definirAviso] = useState<Aviso | null>(null);
  const [salvando, definirSalvando] = useState(false);
  const [agindo, definirAgindo] = useState(false);
  const [formularioAberto, definirFormularioAberto] = useState(false);
  const [aRemoverCredencial, definirARemoverCredencial] = useState(false);
  const [erroDoDialogo, definirErroDoDialogo] = useState<string | null>(null);

  const carregar = useCallback(async (): Promise<EstadoDaTela> => {
    try {
      return { configurada: true, configuracao: await obterIntegracao() };
    } catch (falha: unknown) {
      if (codigoDoErro(falha) === NAO_CONFIGURADA) {
        return { configurada: false };
      }
      throw falha;
    }
  }, []);

  const consulta = useConsulta(carregar);
  const { recarregar } = consulta;

  /** O rascunho aberto, ou o que veio do middleware. */
  function formulario(estado: EstadoDaTela): Rascunho {
    if (rascunho !== null) {
      return rascunho;
    }
    return estado.configurada ? rascunhoDe(estado.configuracao) : RASCUNHO_INICIAL;
  }

  function mudar(campo: keyof Rascunho, valor: string, atual: Rascunho) {
    definirRascunho({ ...atual, [campo]: valor });
  }

  function salvar(atual: Rascunho) {
    const filiais = filiaisDe(atual.codFiliais);
    const encontrados: Record<string, string> = {};

    if (atual.endpointBase.trim() === '') {
      encontrados['endpointBase'] = 'Informe o endereço do middleware Senior.';
    }
    if (atual.identificadorSistema.trim() === '') {
      encontrados['identificadorSistema'] = 'Informe a sigla do sistema de integração.';
    }
    if (!/^\d+$/u.test(atual.codEmp.trim())) {
      encontrados['codEmp'] = 'Informe o código da empresa, só dígitos.';
    }
    if (filiais.length === 0 || filiais.some((filial) => Number.isNaN(filial))) {
      encontrados['codFiliais'] = 'Informe ao menos uma filial, separadas por vírgula.';
    }

    definirErros(encontrados);

    if (Object.keys(encontrados).length > 0) {
      return;
    }

    const dados: DadosDaConfiguracao = {
      sistema: 'SeniorErp5',
      endpointBase: atual.endpointBase.trim(),
      modoAutenticacao: Number(atual.modoAutenticacao),
      codEmp: Number(atual.codEmp.trim()),
      codFiliais: filiais,
      identificadorSistema: atual.identificadorSistema.trim(),
      timeoutMs: Number(atual.timeoutMs),
      tamanhoLote: Number(atual.tamanhoLote),
      usarMock: atual.origem === 'mock',
    };

    definirSalvando(true);
    definirAviso(null);

    salvarIntegracao(dados)
      .then(() => {
        definirSalvando(false);
        definirRascunho(null);
        definirAviso({ tom: 'ok', texto: 'Parâmetros de conexão salvos.' });
        recarregar();
      })
      .catch((falha: unknown) => {
        definirSalvando(false);
        definirAviso({ tom: 'erro', texto: mensagemDoErro(falha) });
      });
  }

  function alternarSituacao(ativo: boolean) {
    definirAgindo(true);
    definirAviso(null);

    definirSituacaoDaIntegracao(ativo)
      .then(() => {
        definirAgindo(false);
        definirAviso({
          tom: 'ok',
          texto: ativo
            ? 'Integração ligada. As operações passam a ser enviadas ao ERP.'
            : 'Integração desligada. Nada mais é enviado ao ERP até religar.',
        });
        recarregar();
      })
      .catch((falha: unknown) => {
        definirAgindo(false);
        definirAviso({ tom: 'erro', texto: mensagemDoErro(falha) });
      });
  }

  function testar() {
    definirAgindo(true);
    definirAviso(null);

    testarConexaoComErp()
      .then((resultado) => {
        definirAgindo(false);
        definirAviso({
          tom: resultado.alcancavel ? 'ok' : 'atencao',
          texto: resultado.alcancavel
            ? `${resultado.detalhe} O teste não verifica a credencial.`
            : resultado.detalhe,
        });
      })
      .catch((falha: unknown) => {
        definirAgindo(false);
        definirAviso({ tom: 'erro', texto: mensagemDoErro(falha) });
      });
  }

  function removerCredencial() {
    definirAgindo(true);
    definirErroDoDialogo(null);

    removerCredencialDoErp()
      .then(() => {
        definirAgindo(false);
        definirARemoverCredencial(false);
        definirAviso({
          tom: 'ok',
          texto: 'Credencial removida do cofre. A integração foi desligada junto.',
        });
        recarregar();
      })
      .catch((falha: unknown) => {
        definirAgindo(false);
        definirErroDoDialogo(mensagemDoErro(falha));
      });
  }

  return (
    <Pagina
      contexto="Configurações"
      titulo="Integração com o ERP"
      acoes={
        podeGerenciar && consulta.dados?.configurada === true ? (
          <Botao altura="media" onClick={testar} disabled={agindo}>
            Testar conexão
          </Botao>
        ) : undefined
      }
    >
      {aviso !== null && <AvisoDaTela tom={aviso.tom}>{aviso.texto}</AvisoDaTela>}

      <Resultado
        consulta={consulta}
        espera="Carregando a configuração da integração…"
        estaVazio={() => false}
        vazio={null}
      >
        {(estado) => {
          const atual = formulario(estado);
          const credencial = estado.configurada ? estado.configuracao.credencial : null;
          const ativa = estado.configurada && estado.configuracao.ativo;

          return (
            <div className="configuracoes">
              {!estado.configurada && (
                <EstadoVazio
                  icone={<IconeConfiguracoes tamanho={20} />}
                  tom="neutro"
                  titulo="Nenhum ERP configurado ainda"
                  detalhe={
                    'Peça ao cliente o endereço do middleware Senior, o usuário de integração, ' +
                    'o código da empresa e as filiais que este armazém movimenta.'
                  }
                />
              )}

              <Cartao titulo="Conexão">
                <div className="configuracoes__grade">
                  <CampoSelecao
                    rotulo="Sistema"
                    valor={atual.sistema}
                    aoMudar={(valor) => {
                      mudar('sistema', valor, atual);
                    }}
                    opcoes={SISTEMAS}
                    desabilitado={!podeGerenciar}
                    ajuda="Hoje só o Senior 5.10.4."
                  />

                  <CampoSelecao
                    rotulo="Origem dos dados"
                    valor={atual.origem}
                    aoMudar={(valor) => {
                      mudar('origem', valor, atual);
                    }}
                    opcoes={ORIGENS}
                    desabilitado={!podeGerenciar}
                    ajuda="Em fixtures, nenhuma requisição sai desta plataforma."
                  />

                  <CampoTexto
                    rotulo="Endereço do middleware Senior"
                    valor={atual.endpointBase}
                    aoMudar={(valor) => {
                      mudar('endpointBase', valor, atual);
                    }}
                    fonte="mono"
                    obrigatorio
                    desabilitado={!podeGerenciar}
                    placeholder="https://erp.cliente.com.br/g5-senior-services"
                    ajuda="Só o caminho base. O nome da porta é montado pelo middleware."
                    {...seHouver('erro', erros['endpointBase'])}
                  />

                  <CampoSelecao
                    rotulo="Modo de autenticação"
                    valor={atual.modoAutenticacao}
                    aoMudar={(valor) => {
                      mudar('modoAutenticacao', valor, atual);
                    }}
                    opcoes={MODOS}
                    desabilitado={!podeGerenciar}
                    ajuda="No modo 0 a senha viaja em claro no XML — o endereço precisa ser https."
                  />

                  <CampoTexto
                    rotulo="Código da empresa"
                    valor={atual.codEmp}
                    aoMudar={(valor) => {
                      mudar('codEmp', valor, atual);
                    }}
                    fonte="mono"
                    obrigatorio
                    desabilitado={!podeGerenciar}
                    placeholder="1"
                    {...seHouver('erro', erros['codEmp'])}
                  />

                  <CampoTexto
                    rotulo="Filiais"
                    valor={atual.codFiliais}
                    aoMudar={(valor) => {
                      mudar('codFiliais', valor, atual);
                    }}
                    fonte="mono"
                    obrigatorio
                    desabilitado={!podeGerenciar}
                    placeholder="1, 2"
                    ajuda="Cada filial é uma consulta ao ERP — o Senior exige uma por chamada."
                    {...seHouver('erro', erros['codFiliais'])}
                  />

                  <CampoTexto
                    rotulo="Sigla do sistema"
                    valor={atual.identificadorSistema}
                    aoMudar={(valor) => {
                      mudar('identificadorSistema', valor, atual);
                    }}
                    fonte="mono"
                    obrigatorio
                    desabilitado={!podeGerenciar}
                    maximoDeCaracteres={15}
                    placeholder="WMS"
                    ajuda="Identifica esta plataforma no ERP. `WMW` é reservada pelo Senior."
                    {...seHouver('erro', erros['identificadorSistema'])}
                  />

                  <CampoTexto
                    rotulo="Tempo limite (ms)"
                    valor={atual.timeoutMs}
                    aoMudar={(valor) => {
                      mudar('timeoutMs', valor, atual);
                    }}
                    fonte="mono"
                    desabilitado={!podeGerenciar}
                  />

                  <CampoTexto
                    rotulo="Tamanho do lote"
                    valor={atual.tamanhoLote}
                    aoMudar={(valor) => {
                      mudar('tamanhoLote', valor, atual);
                    }}
                    fonte="mono"
                    desabilitado={!podeGerenciar}
                    ajuda="Até 50 por envio ao ERP."
                  />
                </div>

                {podeGerenciar && (
                  <div className="configuracoes__acoes">
                    <Botao
                      variante="primario"
                      onClick={() => {
                        salvar(atual);
                      }}
                      disabled={salvando}
                    >
                      {salvando ? 'Salvando…' : 'Salvar parâmetros'}
                    </Botao>
                  </div>
                )}
              </Cartao>

              <Cartao titulo="Credencial do ERP">
                <p className="configuracoes__nota">
                  Usuário e senha ficam em cofre gerenciado, nunca no banco desta plataforma nem no
                  coletor. O painel mostra de quem é a credencial e desde quando — a senha não é
                  exibida em lugar nenhum.
                </p>

                <p className="configuracoes__estado">
                  {credencial?.definida === true ? (
                    <>
                      <ChipStatus tom="concluida" rotulo="Definida" />
                      <span className="configuracoes__detalhe">
                        Usuário <strong>{credencial.usuario ?? '—'}</strong>, desde{' '}
                        {formatarDataHora(credencial.atualizadaEm)}
                      </span>
                    </>
                  ) : (
                    <>
                      <ChipStatus tom="divergencia" rotulo="Não definida" />
                      <span className="configuracoes__detalhe">
                        Sem credencial, a integração não pode ser ligada.
                      </span>
                    </>
                  )}
                </p>

                {podeGerenciar && estado.configurada && (
                  <div className="configuracoes__acoes">
                    <Botao
                      variante="primario"
                      onClick={() => {
                        definirFormularioAberto(true);
                      }}
                    >
                      {credencial?.definida === true
                        ? 'Substituir credencial'
                        : 'Definir credencial'}
                    </Botao>

                    {credencial?.definida === true && (
                      <Botao
                        variante="destrutivo"
                        onClick={() => {
                          definirARemoverCredencial(true);
                        }}
                      >
                        Remover
                      </Botao>
                    )}
                  </div>
                )}
              </Cartao>

              <Cartao titulo="Situação">
                <p className="configuracoes__estado">
                  {ativa ? (
                    <>
                      <ChipStatus tom="concluida" rotulo="Ligada" />
                      <span className="configuracoes__detalhe">
                        As operações registradas seguem para o ERP.
                      </span>
                    </>
                  ) : (
                    <>
                      <ChipStatus tom="desativado" rotulo="Desligada" />
                      <span className="configuracoes__detalhe">
                        Nada é enviado ao ERP. O coletor continua registrando normalmente.
                      </span>
                    </>
                  )}
                </p>

                {podeGerenciar && estado.configurada && (
                  <div className="configuracoes__acoes">
                    <Botao
                      variante={ativa ? 'atencao' : 'sucesso-solido'}
                      onClick={() => {
                        alternarSituacao(!ativa);
                      }}
                      disabled={agindo}
                    >
                      {ativa ? 'Desligar integração' : 'Ligar integração'}
                    </Botao>
                  </div>
                )}
              </Cartao>
            </div>
          );
        }}
      </Resultado>

      {/* Fora do Resultado da integração: a identificação e a leitura por
          imagem têm consulta própria e não somem quando a configuração do ERP
          falha ao carregar. */}
      <div className="configuracoes configuracoes--seguinte">
        <IdentificacaoNoArmazem />
        <LeituraPorImagem />
      </div>

      {formularioAberto && (
        <FormularioDaCredencial
          substituindo={
            consulta.dados?.configurada === true && consulta.dados.configuracao.credencial.definida
          }
          aoFechar={() => {
            definirFormularioAberto(false);
          }}
          aoConcluir={(mensagem) => {
            definirFormularioAberto(false);
            definirAviso({ tom: 'ok', texto: mensagem });
            recarregar();
          }}
        />
      )}

      {aRemoverCredencial && (
        <DialogoConfirmacao
          titulo="Remover a credencial do ERP?"
          rotuloConfirmar="Remover"
          variante="destrutivo"
          agindo={agindo}
          aoConfirmar={removerCredencial}
          aoCancelar={() => {
            definirARemoverCredencial(false);
            definirErroDoDialogo(null);
          }}
        >
          A integração é desligada junto, e nada é enviado ao ERP até que uma credencial nova seja
          informada. As operações já registradas continuam guardadas.
          {erroDoDialogo !== null && (
            <span className="configuracoes__erro-dialogo">{erroDoDialogo}</span>
          )}
        </DialogoConfirmacao>
      )}
    </Pagina>
  );
}
